import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Send, CheckCircle2 } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { QUOTE } from "@/constants/testIds";
import { SERVICES_LIST } from "@/data/siteData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function QuoteForm() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.service || !form.message) {
      toast.error("Please fill out all fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/quote`, form);
      setSuccess(true);
      toast.success("Quote request sent. We'll be in touch shortly.");
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
    } catch (err) {
      console.error(err);
      toast.error("Couldn't submit your request. Please call us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="quote" data-testid={QUOTE.root} className="pe-section">
      <div className="container-pe">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="pe-eyebrow mb-5">Request A Quote</div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white leading-[1]">
              TELL US ABOUT
              <br />
              <span className="text-[var(--pe-gold)]">YOUR JOB.</span>
            </h2>
            <p className="mt-6 text-[var(--pe-muted)] leading-relaxed max-w-md">
              Drop in your details and a quick description of the work.
              We'll respond with a clear estimate — no obligation, no
              high-pressure sales.
            </p>

            <div className="mt-8 space-y-4">
              {[
                "Free estimates",
                "Up-front pricing",
                "Permits handled for you",
                "Clean, code-compliant installs",
              ].map((t) => (
                <div key={t} className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-[var(--pe-gold)] mt-0.5" />
                  <span className="text-sm text-[var(--pe-muted)]">{t}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-7">
            <form
              onSubmit={submit}
              data-testid={QUOTE.form}
              className="pe-card bg-[var(--pe-ink)] !p-6 md:!p-10 space-y-5"
            >
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="font-mono text-[10px] tracking-[0.24em] uppercase text-[var(--pe-muted)] mb-2 block">
                    Name
                  </label>
                  <input
                    data-testid={QUOTE.inputName}
                    className="pe-input"
                    placeholder="Your full name"
                    value={form.name}
                    onChange={update("name")}
                  />
                </div>
                <div>
                  <label className="font-mono text-[10px] tracking-[0.24em] uppercase text-[var(--pe-muted)] mb-2 block">
                    Phone
                  </label>
                  <input
                    data-testid={QUOTE.inputPhone}
                    className="pe-input"
                    placeholder="431-555-1234"
                    value={form.phone}
                    onChange={update("phone")}
                  />
                </div>
              </div>

              <div>
                <label className="font-mono text-[10px] tracking-[0.24em] uppercase text-[var(--pe-muted)] mb-2 block">
                  Email
                </label>
                <input
                  data-testid={QUOTE.inputEmail}
                  type="email"
                  className="pe-input"
                  placeholder="you@example.com"
                  value={form.email}
                  onChange={update("email")}
                />
              </div>

              <div>
                <label className="font-mono text-[10px] tracking-[0.24em] uppercase text-[var(--pe-muted)] mb-2 block">
                  Service
                </label>
                <Select
                  value={form.service}
                  onValueChange={(v) => setForm({ ...form, service: v })}
                >
                  <SelectTrigger
                    data-testid={QUOTE.selectServiceTrigger}
                    className="pe-input !h-auto !py-3 !border-[var(--pe-line-strong)] !bg-[#0c0c0c] !text-white"
                  >
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0c0c0c] border-[var(--pe-line-strong)] text-white">
                    {SERVICES_LIST.map((s) => (
                      <SelectItem
                        key={s.slug}
                        value={s.title}
                        data-testid={QUOTE.selectServiceItem(s.slug)}
                        className="focus:bg-[var(--pe-gold)] focus:text-black"
                      >
                        {s.title}
                      </SelectItem>
                    ))}
                    <SelectItem value="Other / Not Sure" data-testid={QUOTE.selectServiceItem("other")} className="focus:bg-[var(--pe-gold)] focus:text-black">
                      Other / Not Sure
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="font-mono text-[10px] tracking-[0.24em] uppercase text-[var(--pe-muted)] mb-2 block">
                  Project Details
                </label>
                <textarea
                  data-testid={QUOTE.inputMessage}
                  rows={5}
                  className="pe-textarea"
                  placeholder="Tell us a bit about the job — location, timing, any photos you can describe…"
                  value={form.message}
                  onChange={update("message")}
                />
              </div>

              <button
                type="submit"
                data-testid={QUOTE.submit}
                disabled={submitting}
                className="pe-btn pe-btn-primary w-full justify-center disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Sending…
                  </>
                ) : (
                  <>
                    Send Request <Send className="h-4 w-4" />
                  </>
                )}
              </button>

              {success && (
                <p
                  data-testid={QUOTE.successMessage}
                  className="text-sm font-mono tracking-[0.18em] uppercase text-[var(--pe-gold)] flex items-center gap-2"
                >
                  <CheckCircle2 className="h-4 w-4" /> Request received — we'll be in touch.
                </p>
              )}

              <p className="text-[11px] text-[var(--pe-muted)] leading-relaxed pt-2 border-t border-[var(--pe-line)]">
                We use your contact details only to respond to your quote request. Your information is never shared, sold, or used for marketing.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
